import { useMemo, useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, Stack, TextField, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

export interface GrantedItem {
  id: string
  name: string
}

interface Props {
  items: GrantedItem[]
  /** e.g. "site" / "customer" — drives the dialog title and empty state. */
  noun: string
  canManage: boolean
  onRemove?: (id: string) => void
  /** Chips shown inline before collapsing into "+N more". Default 5 —
   *  a tenant with hundreds of sites/customers granted to one person
   *  otherwise turns this section into an unscrollable wall of chips. */
  previewCount?: number
  icon?: React.ReactElement
}

/// Chip row that collapses past `previewCount` into a "+N more" chip
/// opening a searchable popup — the popup is where bulk removal for a
/// long list actually happens; the inline chips stay for the common
/// "one person, a handful of sites" case where a popup would be
/// overkill.
export function GrantedAccessList({ items, noun, canManage, onRemove, previewCount = 5, icon }: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const visible = items.slice(0, previewCount)
  const overflow = items.length - visible.length

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter((i) => i.name.toLowerCase().includes(q))
  }, [items, search])

  const chipSx = {
    height: 28, fontSize: 12, fontWeight: 500,
    '& .MuiChip-deleteIcon': { color: 'text.secondary', '&:hover': { color: 'error.main' } },
  }

  return (
    <>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
        {visible.map((item) => (
          <Chip key={item.id} label={item.name} size="small" icon={icon}
            onDelete={canManage && onRemove ? () => onRemove(item.id) : undefined}
            deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            sx={chipSx} />
        ))}
        {overflow > 0 && (
          <Chip label={`+${overflow} more`} size="small" onClick={() => setOpen(true)}
            sx={{ ...chipSx, cursor: 'pointer', fontWeight: 700, color: 'primary.main',
              bgcolor: (t) => `${t.palette.primary.main}12`,
              '&:hover': { bgcolor: (t) => `${t.palette.primary.main}1F` } }} />
        )}
      </Stack>

      <Dialog open={open} onClose={() => { setOpen(false); setSearch('') }} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2.5 } } }}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 800, pb: 1 }}>
          {items.length} {noun}{items.length === 1 ? '' : 's'} with access
        </DialogTitle>
        <DialogContent sx={{ pt: '4px !important' }}>
          <TextField
            size="small" fullWidth autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${noun}s`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: 13 } }}
          />
          {filtered.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic', py: 2, textAlign: 'center' }}>
              No {noun}s match "{search}".
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 360, overflowY: 'auto', scrollbarWidth: 'thin' }}>
              <Stack spacing={0.25}>
                {filtered.map((item) => (
                  <Stack key={item.id} direction="row" alignItems="center" spacing={1}
                    sx={{ px: 1, py: 0.6, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13 }} noWrap>{item.name}</Typography>
                    {canManage && onRemove && (
                      <IconButton size="small" onClick={() => onRemove(item.id)}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => { setOpen(false); setSearch('') }} sx={{ fontSize: 12.5 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
